import {
  faCalendarDay,
  faChartSimple,
  faComment,
  faEye,
  faFileLines,
  faLock,
  faThumbsUp,
} from '@fortawesome/pro-regular-svg-icons';
import { faThumbTack } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { queryString } from '@olivierpascal/helpers';
import { Anchored, AssistChip, Stack, Text } from '@sixui/ui';
import stylex from '@stylexjs/stylex';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

import type { ITableRowProps } from '~/components/molecules/Table/TableRow';
import type { IApiGetArticleResponseBody } from '~/lib/api/modules/Articles';
import { Thumbnail } from '~/components/atoms/Thumbnail';
import { PublicationStatus } from '~/components/molecules/PublicationStatus';
import { Table } from '~/components/molecules/Table';
import { ArticleListItemMenu } from '~/components/organisms/ArticleListItem/ArticleListItemMenu';
import { getFormattedIapProductPrice } from '~/components/organisms/fields/IapProductSelector/getFormattedIapProductPrice';
import { getIapProductById } from '~/components/organisms/fields/IapProductSelector/getIapProductById';
import { formatEventDate } from '~/helpers/formatEventDate';
import { useDeleteArticleMutation } from '~/lib/api/modules/Articles/hooks/useDeleteArticleMutation';

export type ISearchArticlesListItemProps = ITableRowProps & {
  article: IApiGetArticleResponseBody;
  parentCategoryId?: string;
};

const styles = stylex.create({
  articleIcon: {
    marginRight: '0.25rem',
  },
});

export const SearchArticlesListItem: React.FC<ISearchArticlesListItemProps> = (
  props,
) => {
  const { article, parentCategoryId, ...other } = props;
  const { t, i18n } = useTranslation();
  const deleteArticleMutation = useDeleteArticleMutation();
  const locale = i18n.language;

  const lastPublishedArticle = article;
  const lastDraft = lastPublishedArticle;
  const lastVersion = lastDraft ?? lastPublishedArticle;

  const publicationDate = lastVersion?.publicationDate
    ? DateTime.fromISO(lastVersion.publicationDate)
    : undefined;
  const unpublicationDate = lastVersion?.unpublicationDate
    ? DateTime.fromISO(lastVersion.unpublicationDate)
    : undefined;

  const searchParams = queryString.build({ parent: parentCategoryId });
  const qs = searchParams ? `?${searchParams}` : '';
  const actionHref = `/apps/${article.appId}/articles/${article._id}/update${qs}`;
  const product = article.storeProductId
    ? getIapProductById(article.storeProductId)
    : undefined;
  const articleIcon = lastVersion.isPoll
    ? faChartSimple
    : lastVersion.isEvent
      ? undefined
      : faFileLines;
  const thumbnailUrl = lastVersion.pictures?.[0]?.thumbUrl;

  const hadBadgeOrProduct = !!lastVersion.badges?.list.length || !!product;
  const processing = deleteArticleMutation.isLoading;

  const handleDelete = (): void => {
    // eslint-disable-next-line no-alert
    const doDeletion = confirm(
      t('SearchArticlesListItem.dialog.confirmDeletion.text', {
        title: article.title,
      }),
    );

    if (doDeletion) {
      void deleteArticleMutation
        .mutateAsync({
          appId: article.appId,
          articleId: article._id,
        })
        .catch(() => {
          // TODO: handle error (snackbar)
        });
    }
  };

  return (
    <Table.Row {...other}>
      <Stack as={Table.BodyAction} horizontal gap={3} href={actionHref}>
        <Anchored
          content={
            lastVersion.pinned ? (
              <FontAwesomeIcon icon={faThumbTack} size="lg" />
            ) : undefined
          }
          horizontalOrigin="right"
          verticalOrigin="bottom"
        >
          <Thumbnail url={thumbnailUrl} size="lg" />
        </Anchored>

        <Stack gap={1}>
          <Text variant="title" size="sm" truncateLines={2}>
            {articleIcon ? (
              <FontAwesomeIcon
                {...stylex.props(styles.articleIcon)}
                icon={articleIcon}
              />
            ) : null}
            {lastVersion.title}
          </Text>
          <Stack horizontal gap={2}>
            {lastVersion.isEvent &&
            lastVersion.eventStartDate &&
            lastVersion.eventEndDate ? (
              <Stack horizontal gap={1}>
                <FontAwesomeIcon icon={faCalendarDay} />
                <Text truncateLines={1}>
                  {formatEventDate(
                    lastVersion.eventStartDate,
                    lastVersion.eventEndDate,
                    t,
                    locale,
                  )}
                </Text>
              </Stack>
            ) : lastVersion.createdAt ? (
              DateTime.fromISO(lastVersion.createdAt).toLocaleString(
                DateTime.DATE_FULL,
                { locale },
              )
            ) : null}
            <Stack horizontal gap={1}>
              <FontAwesomeIcon icon={faThumbsUp} />
              {String(lastVersion.likes ?? 0)}
            </Stack>
            <Stack horizontal gap={1}>
              <FontAwesomeIcon icon={faEye} />
              {String(lastVersion.views ?? 0)}
            </Stack>
            <Stack horizontal gap={1}>
              <FontAwesomeIcon icon={faComment} />
              {String(lastVersion.commentsCount ?? 0)}
            </Stack>
          </Stack>
        </Stack>
      </Stack>

      <Table.Body>
        {lastVersion.category ? (
          <AssistChip
            label={lastVersion.category.name}
            href={`/apps/${article.appId}/categories/${lastVersion.category._id}`}
          />
        ) : null}
      </Table.Body>

      <Stack as={Table.Body} horizontal gap={2} justify="end">
        {hadBadgeOrProduct ? (
          <div>
            {lastVersion.badges?.list.length ? (
              <FontAwesomeIcon icon={faLock} size="lg" />
            ) : null}
            {product ? (
              <div>{getFormattedIapProductPrice(product.customerPrice)}</div>
            ) : null}
          </div>
        ) : null}
        <PublicationStatus
          published={article.isPublished}
          publicationDate={publicationDate}
          unpublicationDate={unpublicationDate}
        />
      </Stack>

      <Stack as={Table.Body} horizontal gap={2} justify="end">
        <ArticleListItemMenu
          appId={article.appId}
          articleId={article._id}
          handleDelete={handleDelete}
          processing={processing}
        />
      </Stack>
    </Table.Row>
  );
};
